import React from 'react';
import type { OptionType } from '../../../../util/types';
import { useI18n } from '@whizz/react-i18n';
import { Selector } from '../../../../components';
import Input from '../../../../components/input/Input';

const PRIORITY_COUNTRIES = [ 'GB', 'US', 'NZ', 'TH', 'KE' ];

const sortCountryOptions = (options: OptionType[]): OptionType[] => {
  const priority = options.filter((o) => PRIORITY_COUNTRIES.includes(o.value as string));
  const rest = options.filter((o) => !PRIORITY_COUNTRIES.includes(o.value as string)).sort((a, b) => String(a.value).localeCompare(String(b.value)));
  return [ ...priority.sort((a, b) => PRIORITY_COUNTRIES.indexOf(a.value as string) - PRIORITY_COUNTRIES.indexOf(b.value as string)), ...rest ];
};

export const TableFilters = ({
  search,
  onSearchChange,
  country,
  onCountryChange,
  countryOptions,
  searchColSpan,
  labelColSpan = 2,
  searchPlaceholder = 'Search',
  impactReport,
  onImpactReportChange,
  impactReportOptions,
}: Props) => {
  const { translate } = useI18n();
  const sortedOptions = sortCountryOptions(countryOptions);

  return (
    <tr>
      <th colSpan={searchColSpan}>
        <Input
          id={'search'}
          label={'Search'}
          hideLabel
          placeholder={searchPlaceholder}
          onChange={(e) => onSearchChange(e.target.value)}
          value={search}
        />
      </th>
      <th>
        <div className={'w-48 mr-5'}>
          <Selector
            id={'country'}
            label={translate('pages.ROSTER_MANAGEMENT.STUDENTS.ACTIONS.YEAR_GROUP')}
            placeholder={translate('components.SELECTORS.PLACEHOLDERS.FILTER')}
            options={sortedOptions}
            value={country}
            hideLabel
            isClearable
            onChange={(option: OptionType<string> | null) => onCountryChange(option ?? null)}
            size={'sm'}
          />
        </div>
      </th>
      <th colSpan={labelColSpan} className={'leader-title border-b-4 border-header text-center'}>
        <div className={'font-bold not-italic'}>{translate('pages.ADMIN.LEADERS_EMAILS.TITLE')}</div>
      </th>
      {impactReportOptions && (
        <th className={'impact-report-title border-b-4 border-header text-center'}>
          <div className={'flex flex-col items-center gap-1'}>
            <div className={'font-bold not-italic'}>{translate('pages.ADMIN.IMPACT_REPORTS.TITLE')}</div>
            <div className={'w-36'}>
              <Selector
                id={'impact_report_filter'}
                label={'Impact report filter'}
                hideLabel
                placeholder={'All'}
                options={impactReportOptions}
                value={impactReport ?? null}
                isClearable
                size={'sm'}
                onChange={(option: OptionType<string> | null) => onImpactReportChange?.(option ?? null)}
              />
            </div>
          </div>
        </th>
      )}
    </tr>
  );
};

interface Props {
  search: string;
  onSearchChange: (value: string) => void;
  country: OptionType<string> | null;
  onCountryChange: (option: OptionType<string> | null) => void;
  countryOptions: OptionType[];
  searchColSpan: number;
  labelColSpan?: number;
  searchPlaceholder?: string;
  impactReport?: OptionType<string> | null;
  onImpactReportChange?: (option: OptionType<string> | null) => void;
  impactReportOptions?: OptionType[];
}
